import React, { Suspense, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  getCardPlacement,
  getCoverLayer,
  getLineAnchorToCard,
  imageToFramePercent,
  Size,
  CardPlacement,
} from './markerCoords.ts';
import './App.css';
import InfoCardClose from './InfoCardClose.tsx';

const SocialFeedSidebar = React.lazy(() => import('./SocialFeedSidebar.tsx'));
const BesertBydraSkinRain = React.lazy(() => import('./BesertBydraSkinRain.tsx'));
const BirthdayBalloons = React.lazy(() => import('./BirthdayBalloons.tsx'));
const CheatNelioClipBackground = React.lazy(() => import('./CheatNelioClipBackground.tsx'));
const ScrollScrubVideo = React.lazy(() => import('./ScrollScrubVideo.tsx'));
const MatchHistory = React.lazy(() => import('./MatchHistory.tsx'));
const SocialsCard = React.lazy(() => import('./SocialsCard.tsx'));
const OtherSitesCard = React.lazy(() => import('./OtherSitesCard.tsx'));
const PoopaiCard = React.lazy(() => import('./PoopaiCard.tsx'));

type HeroImages = {
  webp: string;
  webpMobile: string;
  png: string;
};

const HERO_IMAGES = {
  day: {
    webp: `${process.env.PUBLIC_URL}/areal-view.webp`,
    webpMobile: `${process.env.PUBLIC_URL}/areal-view-mobile.webp`,
    png: `${process.env.PUBLIC_URL}/areal-view.png?v=00216001`,
  },
  night: {
    webp: `${process.env.PUBLIC_URL}/areal-view-v3.webp`,
    webpMobile: `${process.env.PUBLIC_URL}/areal-view-v3-mobile.webp`,
    png: `${process.env.PUBLIC_URL}/areal-view-v3.png?v=v3`,
  },
} satisfies Record<'day' | 'night', HeroImages>;
const BERLIN_TZ = 'Europe/Berlin';

/** Red Desert Hydra card center in the 1920×1080 end frame. */
const DESERT_HYDRA_ALIGN_X = 992 / 1920;
/** Item row in that same frame, used for the case marker. */
const DESERT_HYDRA_CASE_LINE = { top: 412 / 1080, bottom: 718 / 1080 };

function getBerlinHour(now = Date.now()): number {
  return Number(
    new Intl.DateTimeFormat('en-GB', {
      timeZone: BERLIN_TZ,
      hour: 'numeric',
      hour12: false,
    }).format(new Date(now))
  );
}

function isBirthday(now = Date.now()): boolean {
  const birthday = new Intl.DateTimeFormat('en-GB', {
    timeZone: BERLIN_TZ,
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(new Date(now));
  const values = Object.fromEntries(
    birthday.filter(({ type }) => type !== 'literal').map(({ type, value }) => [type, value])
  );

  return values.month === '07' && values.day === '13';
}

function isNightRoute(): boolean {
  const path = window.location.pathname.replace(/\/+$/, '') || '/';
  return path === '/night';
}

function isNightHours(now = Date.now()): boolean {
  const hour = getBerlinHour(now);
  return hour >= 20 || hour < 6;
}

function resolveHeroImages(now = Date.now()): HeroImages {
  if (isNightRoute() || isNightHours(now)) return HERO_IMAGES.night;
  return HERO_IMAGES.day;
}

function useDesktopSidebar() {
  const [showSidebar, setShowSidebar] = useState(() => {
    if (typeof window.matchMedia !== 'function') return true;
    return window.matchMedia('(min-width: 901px)').matches;
  });

  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return undefined;

    const media = window.matchMedia('(min-width: 901px)');
    const onChange = () => setShowSidebar(media.matches);
    media.addEventListener('change', onChange);
    return () => media.removeEventListener('change', onChange);
  }, []);

  return showSidebar;
}

type MarkerType = 'bomb' | 'poi';

type MarkerLink = {
  url: string;
  label: string;
};

type Marker = {
  id: string;
  label: string;
  tag: string;
  description: string;
  type: MarkerType;
  x: number;
  y: number;
  previewImage?: string;
  link?: MarkerLink;
  cardTheme?: 'besert-bydra' | 'cheat-nelio' | 'socials' | 'other-sites' | 'poopai';
  cardPlacement?: CardPlacement;
};

const markers: Marker[] = [
  {
    id: 'marker-1',
    label: 'POOPAI',
    tag: 'POOPAI',
    description:
      'Discord bot stats, changelog and usage tracking. Messages, tokens, images, videos and more. All on one chaotic stats page.',
    type: 'poi',
    x: 58.2,
    y: 14.5,
    cardTheme: 'poopai',
  },
  {
    id: 'marker-2',
    label: 'Besert Bydra',
    tag: 'BESERT BYDRA',
    description:
      'A Gambling-Clicker Game where gray rarity is the most common and red rarity (besert bydra lol) is the rarest one. Battle your friends in a 1v1 gamemode or grind (me) the Singleplayer!',
    type: 'poi',
    x: 47.7,
    y: 24,
    previewImage: '/besert-bydra-preview.png',
    link: {
      url: 'https://besertbydra.nelio.gg/',
      label: 'Open Besert Bydra',
    },
    cardTheme: 'besert-bydra',
    cardPlacement: 'left',
  },
  {
    id: 'marker-3',
    label: 'Socials',
    tag: 'SOCIALS',
    description: '',
    type: 'bomb',
    x: 49.9,
    y: 56.2,
    cardTheme: 'socials',
  },
  {
    id: 'marker-4',
    label: 'Other Sites',
    tag: 'OTHER SITES',
    description: '*farts cutley*',
    type: 'poi',
    x: 54.5,
    y: 72.4,
    cardTheme: 'other-sites',
    cardPlacement: 'left',
  },
  {
    id: 'marker-5',
    label: 'Cheat.Nelio.GG',
    tag: 'CHEAT.NELIO.GG',
    description:
      'Win every Game and hit Fake Clips. One of a kind Spinbot in collaboration with IRAQISPIN. Most Accurate Noscope Aimbot, reliable Auto-Edgebug & the world\'s first Flick-Aimbot.',
    type: 'poi',
    x: 79,
    y: 56.4,
    link: {
      url: 'https://cheat.nelio.gg/',
      label: 'Open Cheat.Nelio.GG',
    },
    cardTheme: 'cheat-nelio',
  },
];

type LineCoords = {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  length: number;
};

function App() {
  const mapScrollRef = useRef<HTMLDivElement>(null);
  const frameRef = useRef<HTMLDivElement>(null);
  const markerRef = useRef<HTMLButtonElement>(null);
  const cardRef = useRef<HTMLElement>(null);
  const imageWrapRef = useRef<HTMLDivElement>(null);
  const didCenterMobileMap = useRef(false);
  const [heroImages, setHeroImages] = useState(resolveHeroImages);
  const showSidebar = useDesktopSidebar();
  const isMobileMap = !showSidebar;
  const [contentOpen, setContentOpen] = useState(false);
  const [activeMarker, setActiveMarker] = useState<string | null>(null);
  const [imageSize, setImageSize] = useState<Size>({ width: 0, height: 0 });
  const [frameSize, setFrameSize] = useState<Size>({ width: 0, height: 0 });
  const [mapViewport, setMapViewport] = useState<Size>({ width: 0, height: 0 });
  const [lineCoords, setLineCoords] = useState<LineCoords | null>(null);
  const [showScrollCue, setShowScrollCue] = useState(false);

  useEffect(() => {
    const timer = window.setTimeout(() => setShowScrollCue(true), 6500);
    return () => window.clearTimeout(timer);
  }, []);

  useLayoutEffect(() => {
    // Never land on the scrub via hash restore; map hero is the entry point.
    if (window.location.hash === '#dotted-nuke' || window.location.hash === '#desert-hydra') {
      window.history.replaceState(null, '', window.location.pathname + window.location.search);
    }

    const hash = window.location.hash;
    if (hash === '#match-history' || hash === '#nuke-wins' || hash === '#nuke-leaderboard') {
      return;
    }

    const lockTop = () => window.scrollTo(0, 0);
    lockTop();
    const raf = window.requestAnimationFrame(lockTop);
    const timers = [50, 200, 600, 1200].map((delay) => window.setTimeout(lockTop, delay));

    return () => {
      window.cancelAnimationFrame(raf);
      timers.forEach((timer) => window.clearTimeout(timer));
    };
  }, []);

  const selectedMarker = markers.find((marker) => marker.id === activeMarker) ?? null;

  useEffect(() => {
    const updateHeroImages = () => setHeroImages(resolveHeroImages());

    updateHeroImages();
    const interval = window.setInterval(updateHeroImages, 60 * 60 * 1000);
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        updateHeroImages();
      }
    };

    window.addEventListener('popstate', updateHeroImages);
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      window.clearInterval(interval);
      window.removeEventListener('popstate', updateHeroImages);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, []);

  useEffect(() => {
    const frame = frameRef.current;
    if (!frame) return;

    const updateSize = () => {
      const rect = frame.getBoundingClientRect();
      setFrameSize({ width: rect.width, height: rect.height });
    };

    updateSize();
    const observer = new ResizeObserver(updateSize);
    observer.observe(frame);
    window.addEventListener('resize', updateSize);

    return () => {
      observer.disconnect();
      window.removeEventListener('resize', updateSize);
    };
  }, []);

  useLayoutEffect(() => {
    const map = mapScrollRef.current;
    if (!map) return;

    const updateViewport = () => {
      setMapViewport({ width: map.clientWidth, height: map.clientHeight });
    };

    updateViewport();
    const observer = new ResizeObserver(updateViewport);
    observer.observe(map);
    window.addEventListener('resize', updateViewport);

    return () => {
      observer.disconnect();
      window.removeEventListener('resize', updateViewport);
    };
  }, []);

  const handleImageLoad = (event: React.SyntheticEvent<HTMLImageElement>) => {
    const { naturalWidth, naturalHeight } = event.currentTarget;
    if (naturalWidth > 0 && naturalHeight > 0) {
      setImageSize({ width: naturalWidth, height: naturalHeight });
    }
  };

  useEffect(() => {
    const img = imageWrapRef.current?.querySelector('img');
    if (!img?.complete || !img.naturalWidth || !img.naturalHeight) return;
    setImageSize({ width: img.naturalWidth, height: img.naturalHeight });
  }, []);

  // Mobile: size the pannable frame to the cover layer of the visible map viewport
  // so every marker is reachable (portrait cover crops left/right on a fixed frame).
  const coverSource =
    isMobileMap && mapViewport.width > 0 && mapViewport.height > 0 ? mapViewport : frameSize;
  const rawCoverLayer = getCoverLayer(imageSize, coverSource);
  const coverLayer = isMobileMap
    ? { left: 0, top: 0, width: rawCoverLayer.width, height: rawCoverLayer.height }
    : rawCoverLayer;
  const coverLayerStyle = useMemo(
    () =>
      isMobileMap
        ? {
            left: '0px',
            top: '0px',
            width: '100%',
            height: '100%',
          }
        : {
            left: `${coverLayer.left}px`,
            top: `${coverLayer.top}px`,
            width: `${coverLayer.width}px`,
            height: `${coverLayer.height}px`,
          },
    [isMobileMap, coverLayer.left, coverLayer.top, coverLayer.width, coverLayer.height]
  );
  const mobileFrameStyle = useMemo(() => {
    if (!isMobileMap || rawCoverLayer.width < 1 || mapViewport.height < 1) return undefined;
    // Match viewport height so the map never scrolls vertically; only sideways.
    return {
      width: `${rawCoverLayer.width}px`,
      height: `${mapViewport.height}px`,
    } as React.CSSProperties;
  }, [isMobileMap, rawCoverLayer.width, mapViewport.height]);

  const getFramePosition = (marker: Marker) =>
    imageToFramePercent({ x: marker.x, y: marker.y }, imageSize, frameSize);

  const updateConnectorLine = useCallback(() => {
    if (!frameRef.current || !markerRef.current || !cardRef.current || !selectedMarker) {
      setLineCoords(null);
      return;
    }

    const frame = frameRef.current.getBoundingClientRect();
    const dot = markerRef.current.querySelector('.map-marker__dot');
    if (!dot) return;

    const dotRect = dot.getBoundingClientRect();
    const cardRect = cardRef.current.getBoundingClientRect();
    const x1 = dotRect.left + dotRect.width / 2 - frame.left;
    const y1 = dotRect.top + dotRect.height / 2 - frame.top;
    const anchor = getLineAnchorToCard(
      x1,
      y1,
      cardRect.left - frame.left,
      cardRect.top - frame.top,
      cardRect.width,
      cardRect.height
    );
    const x2 = anchor.x;
    const y2 = anchor.y;
    const length = Math.hypot(x2 - x1, y2 - y1);

    setLineCoords({ x1, y1, x2, y2, length });
  }, [selectedMarker]);

  const selectMarker = (id: string) => {
    setContentOpen(false);
    setActiveMarker((current) => (current === id ? null : id));
  };

  const closeMarker = () => {
    setActiveMarker(null);
  };

  const openContent = () => {
    setActiveMarker(null);
    setContentOpen(true);
  };

  const closeContent = () => {
    setContentOpen(false);
  };

  const handleMapClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if ((event.target as HTMLElement).closest('.map-marker, .info-card')) return;
    setActiveMarker(null);
  };

  useEffect(() => {
    if (!contentOpen) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setContentOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [contentOpen]);

  useEffect(() => {
    if (!contentOpen || !isMobileMap) return;

    const html = document.documentElement;
    const { body } = document;
    const prevHtmlOverflow = html.style.overflow;
    const prevBodyOverflow = body.style.overflow;
    const scrollY = window.scrollY;

    html.style.overflow = 'hidden';
    body.style.overflow = 'hidden';
    // Keep the map hero locked under the sheet; iOS otherwise rubber-bands the page.
    body.style.position = 'fixed';
    body.style.top = `-${scrollY}px`;
    body.style.left = '0';
    body.style.right = '0';
    body.style.width = '100%';

    const allowsTouchScroll = (target: EventTarget | null) => {
      if (!(target instanceof Element)) return false;
      return Boolean(target.closest('.tactical-social__list, .tactical-social__filter-menu'));
    };

    const onTouchMove = (event: TouchEvent) => {
      if (!allowsTouchScroll(event.target)) {
        event.preventDefault();
      }
    };

    const onWheel = (event: WheelEvent) => {
      if (!allowsTouchScroll(event.target)) {
        event.preventDefault();
      }
    };

    document.addEventListener('touchmove', onTouchMove, { passive: false });
    document.addEventListener('wheel', onWheel, { passive: false });

    return () => {
      html.style.overflow = prevHtmlOverflow;
      body.style.overflow = prevBodyOverflow;
      body.style.position = '';
      body.style.top = '';
      body.style.left = '';
      body.style.right = '';
      body.style.width = '';
      document.removeEventListener('touchmove', onTouchMove);
      document.removeEventListener('wheel', onWheel);
      window.scrollTo(0, scrollY);
    };
  }, [contentOpen, isMobileMap]);

  const selectedPosition = selectedMarker ? getFramePosition(selectedMarker) : null;
  const cardPlacement =
    selectedMarker?.cardPlacement ??
    (selectedPosition ? getCardPlacement(selectedPosition) : 'right');

  useLayoutEffect(() => {
    if (!isMobileMap) return;
    if (didCenterMobileMap.current) return;

    const scroller = mapScrollRef.current;
    if (!scroller || coverLayer.width < 1 || coverLayer.height < 1) return;
    if (mapViewport.width < 1 || mapViewport.height < 1) return;
    // Wait until the expanded frame is actually in the layout.
    if (scroller.scrollWidth <= scroller.clientWidth + 8) return;

    const focus = markers.find((marker) => marker.id === 'marker-3') ?? markers[2];
    const markerX = (focus.x / 100) * coverLayer.width;

    const center = () => {
      scroller.scrollTo({
        left: Math.max(0, markerX - scroller.clientWidth / 2),
        top: 0,
        behavior: 'auto',
      });
    };

    center();
    requestAnimationFrame(center);
    didCenterMobileMap.current = true;
  }, [isMobileMap, coverLayer.width, coverLayer.height, mapViewport.width, mapViewport.height]);

  useEffect(() => {
    if (!isMobileMap) {
      didCenterMobileMap.current = false;
      return;
    }

    if (didCenterMobileMap.current) return;
    const scroller = mapScrollRef.current;
    if (!scroller || coverLayer.width < 1) return;

    let attempts = 0;
    const tryCenter = () => {
      attempts += 1;
      if (didCenterMobileMap.current) return;
      if (scroller.scrollWidth <= scroller.clientWidth + 8) {
        if (attempts < 20) window.setTimeout(tryCenter, 50);
        return;
      }

      const focus = markers.find((marker) => marker.id === 'marker-3') ?? markers[2];
      const markerX = (focus.x / 100) * coverLayer.width;
      scroller.scrollTo({
        left: Math.max(0, markerX - scroller.clientWidth / 2),
        top: 0,
        behavior: 'auto',
      });
      didCenterMobileMap.current = true;
    };

    const timer = window.setTimeout(tryCenter, 0);
    return () => window.clearTimeout(timer);
  }, [isMobileMap, coverLayer.width, coverLayer.height, mapViewport.width, mapViewport.height]);

  useEffect(() => {
    if (!selectedMarker || !mapScrollRef.current || coverLayer.width === 0) {
      return;
    }

    if (!isMobileMap) return;

    const markerX = coverLayer.left + (selectedMarker.x / 100) * coverLayer.width;
    const scroller = mapScrollRef.current;

    // Keep the pin horizontally centered; vertical page scroll handles the rest.
    window.requestAnimationFrame(() => {
      scroller.scrollTo({
        left: Math.max(0, markerX - scroller.clientWidth / 2),
        top: 0,
        behavior: 'smooth',
      });
    });
  }, [selectedMarker, isMobileMap, coverLayer.left, coverLayer.width]);

  useLayoutEffect(() => {
    if (!selectedMarker) {
      setLineCoords(null);
      return;
    }

    updateConnectorLine();

    const imageWrap = imageWrapRef.current;
    const onLayoutChange = () => window.requestAnimationFrame(updateConnectorLine);

    window.addEventListener('resize', onLayoutChange);
    imageWrap?.addEventListener('transitionend', onLayoutChange);

    const timers = [0, 80, 300, 700].map((delay) =>
      window.setTimeout(updateConnectorLine, delay)
    );

    return () => {
      window.removeEventListener('resize', onLayoutChange);
      imageWrap?.removeEventListener('transitionend', onLayoutChange);
      timers.forEach((timer) => window.clearTimeout(timer));
    };
  }, [selectedMarker, frameSize, updateConnectorLine, cardPlacement]);

  useEffect(() => {
    const onScroll = () => {
      if (window.scrollY > 48) {
        setActiveMarker(null);
        setContentOpen(false);
      }
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const scrollToDottedNuke = () => {
    document.getElementById('dotted-nuke')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="site">
    <div className={`tactical${selectedMarker ? ' tactical--card-open' : ''}`}>
      {isBirthday() && (
        <Suspense fallback={null}>
          <BirthdayBalloons />
        </Suspense>
      )}

      <aside
        id="my-content-sheet"
        className={`tactical-sidebar${contentOpen ? ' tactical-sidebar--sheet-open' : ''}`}
        aria-hidden={isMobileMap ? !contentOpen : undefined}
      >
        <div className="tactical-sidebar__sheet-handle" aria-hidden="true" />
        <Suspense fallback={null}>
          {(showSidebar || contentOpen) && (
            <SocialFeedSidebar onClose={isMobileMap && contentOpen ? closeContent : undefined} />
          )}
        </Suspense>
      </aside>

      <footer className="tactical-footer">
        <div className="tactical-footer__brand">
          <strong>DE_NUKE</strong>
          <span>Tactical Overview</span>
          <small>Select a location</small>
        </div>

        <div className="tactical-footer__steps">
          {markers.map((marker, index) => (
            <button
              key={marker.id}
              type="button"
              className={`tactical-footer__step ${
                activeMarker === marker.id ? 'tactical-footer__step--active' : ''
              }`}
              onClick={() => selectMarker(marker.id)}
            >
              {String(index + 1).padStart(2, '0')}
            </button>
          ))}
        </div>

        <div className="tactical-footer__legend">
          <span>
            <i className="legend-dot legend-dot--bomb" />
            Other
          </span>
          <span>
            <i className="legend-dot legend-dot--poi" />
            Websites
          </span>
        </div>
        <button
          type="button"
          className={`tactical-footer__content${contentOpen ? ' tactical-footer__content--active' : ''}`}
          aria-expanded={contentOpen}
          aria-controls="my-content-sheet"
          onClick={() => (contentOpen ? closeContent() : openContent())}
        >
          content
        </button>
      </footer>

      <button
        type="button"
        className={`tactical-scroll-cue${showScrollCue ? ' tactical-scroll-cue--visible' : ''}`}
        onClick={scrollToDottedNuke}
        aria-label="Scroll to dotted nuke"
        tabIndex={showScrollCue ? 0 : -1}
      >
        <span className="tactical-scroll-cue__arrows" aria-hidden="true">
          <span />
          <span />
        </span>
      </button>

      {selectedMarker && isMobileMap && (
        <button
          type="button"
          className="tactical-backdrop"
          aria-label="Close card"
          onClick={closeMarker}
        />
      )}

      {contentOpen && isMobileMap && (
        <button
          type="button"
          className="tactical-content-backdrop"
          aria-label="Close content"
          onClick={closeContent}
        />
      )}

      <div ref={mapScrollRef} className="tactical-map">
        <div
          ref={frameRef}
          className={`tactical-map__frame ${selectedMarker ? 'tactical-map__frame--focused' : ''}`}
          style={
            {
              ...(mobileFrameStyle ?? {}),
              ...(selectedPosition
                ? ({
                    '--focus-x': `${selectedPosition.x}%`,
                    '--focus-y': `${selectedPosition.y}%`,
                  } as React.CSSProperties)
                : {}),
            } as React.CSSProperties
          }
          onClick={handleMapClick}
        >
          <div className="tactical-map__vignette" aria-hidden="true" />
          <div ref={imageWrapRef} className="tactical-map__image-wrap">
            <div className="tactical-map__cover-layer" style={coverLayerStyle}>
              <picture>
                <source
                  media="(max-width: 900px)"
                  srcSet={heroImages.webpMobile}
                  type="image/webp"
                />
                <source srcSet={heroImages.webp} type="image/webp" />
                <img
                  src={heroImages.png}
                  alt="de_nuke aerial view"
                  className="tactical-map__image"
                  onLoad={handleImageLoad}
                  fetchPriority="high"
                  decoding="async"
                />
              </picture>

              {markers.map((marker) => {
                const isActive = activeMarker === marker.id;

                return (
                  <button
                    key={marker.id}
                    ref={isActive ? markerRef : undefined}
                    type="button"
                    className={`map-marker map-marker--${marker.type} ${
                      isActive ? 'map-marker--active' : ''
                    }`}
                    style={{ left: `${marker.x}%`, top: `${marker.y}%` }}
                    onClick={(event) => {
                      event.stopPropagation();
                      selectMarker(marker.id);
                    }}
                    aria-label={marker.label}
                  >
                    <span className="map-marker__label">{marker.tag}</span>
                    <span className="map-marker__dot-wrap">
                      <span className="map-marker__pulse" />
                      <span className="map-marker__ring" />
                      <span className="map-marker__dot" />
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {selectedMarker && (
            <>
              {lineCoords && frameSize.width > 0 && (
                <svg
                  key={`line-${selectedMarker.id}`}
                  className="info-card__line"
                  width={frameSize.width}
                  height={frameSize.height}
                  viewBox={`0 0 ${frameSize.width} ${frameSize.height}`}
                  aria-hidden="true"
                >
                  <line
                    className="info-card__line-path"
                    x1={lineCoords.x1}
                    y1={lineCoords.y1}
                    x2={lineCoords.x2}
                    y2={lineCoords.y2}
                    style={
                      {
                        strokeDasharray: lineCoords.length,
                        strokeDashoffset: lineCoords.length,
                      } as React.CSSProperties
                    }
                  />
                </svg>
              )}
              <Suspense fallback={null}>
                {selectedMarker.cardTheme === 'poopai' ? (
                  <PoopaiCard
                    ref={cardRef}
                    placement={cardPlacement}
                    label={selectedMarker.label}
                    description={selectedMarker.description}
                    onClose={closeMarker}
                  />
                ) : selectedMarker.cardTheme === 'socials' ? (
                  <SocialsCard
                    ref={cardRef}
                    placement={cardPlacement}
                    tag={selectedMarker.tag}
                    label={selectedMarker.label}
                    markerType={selectedMarker.type}
                    onClose={closeMarker}
                  />
                ) : selectedMarker.cardTheme === 'other-sites' ? (
                  <OtherSitesCard
                    ref={cardRef}
                    placement={cardPlacement}
                    tag={selectedMarker.tag}
                    label={selectedMarker.label}
                    description={selectedMarker.description}
                    markerType={selectedMarker.type}
                    onClose={closeMarker}
                  />
                ) : selectedMarker.cardTheme ? (
                  <article
                    key={selectedMarker.id}
                    ref={cardRef}
                    className={`info-card info-card--${cardPlacement} info-card--${selectedMarker.cardTheme}`}
                  >
                    <InfoCardClose onClose={closeMarker} />
                    {selectedMarker.cardTheme === 'besert-bydra' && <BesertBydraSkinRain />}
                    {selectedMarker.cardTheme === 'cheat-nelio' && <CheatNelioClipBackground />}
                    <div className="info-card__body">
                      {selectedMarker.cardTheme !== 'cheat-nelio' && (
                        <span className={`info-card__tag info-card__tag--${selectedMarker.type}`}>
                          {selectedMarker.tag}
                        </span>
                      )}
                      {selectedMarker.cardTheme === 'cheat-nelio' ? (
                        <img
                          src="/nelioggFixedSize.gif"
                          alt={selectedMarker.label}
                          className="info-card__title-logo"
                        />
                      ) : (
                        <h2>{selectedMarker.label}</h2>
                      )}
                      <p>{selectedMarker.description}</p>
                      {selectedMarker.cardTheme !== 'cheat-nelio' && (
                        <div className="info-card__preview">
                          <img src={selectedMarker.previewImage ?? ''} alt="" loading="lazy" decoding="async" />
                        </div>
                      )}
                      {selectedMarker.link && (
                        <a
                          href={selectedMarker.link.url}
                          target="_blank"
                          rel="noreferrer"
                          className="info-card__cta"
                        >
                          <span>{selectedMarker.link.label}</span>
                          <svg
                            className="info-card__cta-icon"
                            viewBox="0 0 24 24"
                            aria-hidden="true"
                          >
                            <path d="M15 3h6v6" />
                            <path d="M10 14 21 3" />
                            <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                          </svg>
                        </a>
                      )}
                    </div>
                  </article>
                ) : (
                  <article
                    key={selectedMarker.id}
                    ref={cardRef}
                    className={`info-card info-card--${cardPlacement}${
                      selectedMarker.cardTheme ? ` info-card--${selectedMarker.cardTheme}` : ''
                    }`}
                  >
                    <InfoCardClose onClose={closeMarker} />
                    <span className={`info-card__tag info-card__tag--${selectedMarker.type}`}>
                      {selectedMarker.tag}
                    </span>
                    <h2>{selectedMarker.label}</h2>
                    <p>{selectedMarker.description}</p>
                    <div className="info-card__preview">
                      <img
                        src={
                          selectedMarker.previewImage ?? heroImages.png
                        }
                        alt=""
                        loading="lazy"
                        decoding="async"
                        style={
                          selectedMarker.previewImage
                            ? undefined
                            : {
                                objectPosition: `${selectedMarker.x}% ${selectedMarker.y}%`,
                              }
                        }
                      />
                    </div>
                    {selectedMarker.link && (
                      <a
                        href={selectedMarker.link.url}
                        target="_blank"
                        rel="noreferrer"
                        className="info-card__cta"
                      >
                        {selectedMarker.link.label}
                      </a>
                    )}
                  </article>
                )}
              </Suspense>
            </>
          )}
        </div>
      </div>
    </div>

    <div
      className="site-lower"
      style={{
        backgroundImage: `url(${process.env.PUBLIC_URL}/desert-hydra-pattern.webp?v=3)`,
      }}
    >
      <Suspense fallback={<section className="scroll-scrub scroll-scrub--dotted-nuke" id="dotted-nuke" aria-hidden="true" />}>
        <ScrollScrubVideo
          id="dotted-nuke"
          ariaLabel="dotted nuke"
          className="scroll-scrub--dotted-nuke"
          src={`${process.env.PUBLIC_URL}/dotted_nuke_scrub.mp4`}
          desktopDistance={2100}
          mobileDistance={1200}
        />
      </Suspense>

      <Suspense fallback={<section className="match-history" id="match-history" aria-hidden="true" />}>
        <MatchHistory />
      </Suspense>

      <Suspense fallback={<section className="scroll-scrub scroll-scrub--desert-hydra" id="desert-hydra" aria-hidden="true" />}>
        <ScrollScrubVideo
          id="desert-hydra"
          ariaLabel="desert hydra"
          className="scroll-scrub--desert-hydra"
          src={`${process.env.PUBLIC_URL}/deserthydra_scroll.mp4?v=1080`}
          overlaySrc={`${process.env.PUBLIC_URL}/deserthydra_react.mp4`}
          overlayDelay={0.12}
          overlayDelayMobile={0.3}
          overlaySpeed={0.9}
          alignX={DESERT_HYDRA_ALIGN_X}
          caseLine={DESERT_HYDRA_CASE_LINE}
          patternBackdrop
          desktopDistance={2600}
          mobileDistance={1500}
        />
      </Suspense>
    </div>
    </div>
  );
}

export default App;
